import React, { useState } from 'react'
import { Eye, EyeOff, Mail, Lock, Sparkles } from 'lucide-react'
import { authClient } from '../lib/auth-client'
import PillButton from './PillButton'
import { TextInput } from '@astryxdesign/core/TextInput'
import loginImage from '../assets/auth/login-modal-image.png'

export interface AuthFormProps {
  initialMode?: 'signIn' | 'signUp'
}

const GOLD = '#e9b44c'
const MUTED = '#a6b0d1'
const PANEL_BORDER = 'rgba(255, 255, 255, 0.1)'

export default function AuthForm({ initialMode = 'signIn' }: AuthFormProps): React.JSX.Element {
  const [isSignUp, setIsSignUp] = useState(initialMode === 'signUp')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!email || !password) {
      setError('Please fill in all fields.')
      return
    }

    if (isSignUp && !name) {
      setError('Please enter your name.')
      return
    }

    setLoading(true)

    try {
      if (isSignUp) {
        const { error: signUpError } = await authClient.signUp.email({
          email,
          password,
          name,
          callbackURL: `${window.location.origin}/home`
        })
        if (signUpError) {
          setError(signUpError.message || 'Failed to sign up.')
        }
      } else {
        const { error: signInError } = await authClient.signIn.email({
          email,
          password,
          callbackURL: `${window.location.origin}/home`
        })
        if (signInError) {
          setError(signInError.message || 'Failed to sign in. Please check your credentials.')
        }
      }
    } catch (err: any) {
      console.error(err)
      setError(err.message || 'An unexpected error occurred.')
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setError(null)
    setLoading(true)
    try {
      const { error: socialError } = await authClient.signIn.social({
        provider: 'google',
        callbackURL: `${window.location.origin}/home`
      })
      if (socialError) {
        setError(socialError.message || 'Failed to initialize Google login.')
      }
    } catch (err: any) {
      console.error(err)
      setError(err.message || 'An unexpected error occurred during Google sign-in.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="auth-card" style={{ animation: 'fadeIn 0.5s ease-out' }}>
      {/* Left panel: brand image */}
      <div
        className="auth-card-image"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(11, 16, 38, 0.9) 0%, rgba(11, 16, 38, 0.55) 20%, transparent 45%), url(${loginImage})`
        }}
      >
        <div className="auth-card-image-content">
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0px' }}
          >
            <span style={{ color: GOLD, fontSize: '20px', letterSpacing: '0.04em' }}>
              Welcome to
            </span>
            <div
              style={{
                fontFamily: 'Playfair Display, Georgia, serif',
                fontSize: '36px',
                fontWeight: 700
              }}
            >
              <span style={{ color: '#5b7fff' }}>9</span>
              <span style={{ color: '#fff' }}>nights</span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '160px' }}>
            <span style={{ flex: 1, height: '1px', background: PANEL_BORDER }} />
            <Sparkles size={12} color={GOLD} />
            <span style={{ flex: 1, height: '1px', background: PANEL_BORDER }} />
          </div>
        </div>
      </div>

      {/* Right panel: form */}
      <div className="auth-card-fields">
        <div className="auth-tabs">
          <button
            type="button"
            className="auth-tab"
            style={{
              color: isSignUp ? MUTED : '#fff',
              borderBottomColor: isSignUp ? 'transparent' : GOLD
            }}
            onClick={() => {
              setIsSignUp(false)
              setError(null)
            }}
          >
            Log in
          </button>
          <button
            type="button"
            className="auth-tab"
            style={{
              color: isSignUp ? '#fff' : MUTED,
              borderBottomColor: isSignUp ? GOLD : 'transparent'
            }}
            onClick={() => {
              setIsSignUp(true)
              setError(null)
            }}
          >
            Sign up
          </button>
        </div>

        {error && (
          <div
            style={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              borderRadius: '8px',
              padding: '12px 16px',
              fontSize: '13px',
              color: 'rgb(239, 68, 68)',
              marginBottom: '16px',
              lineHeight: '1.4'
            }}
          >
            {error}
          </div>
        )}

        <form
          onSubmit={handleEmailAuth}
          style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
        >
          {isSignUp && (
            <TextInput
              label="Full Name"
              placeholder="Enter your name"
              value={name}
              onChange={setName}
              type="text"
              isDisabled={loading}
              className="auth-input"
            />
          )}

          <TextInput
            label="Email"
            placeholder="Enter your email"
            value={email}
            onChange={setEmail}
            type="email"
            startIcon={<Mail size={16} />}
            isDisabled={loading}
            className="auth-input"
          />

          <div style={{ position: 'relative' }}>
            <TextInput
              label="Password"
              placeholder={isSignUp ? 'Choose a strong password' : 'Enter your password'}
              value={password}
              onChange={setPassword}
              type={showPassword ? 'text' : 'password'}
              startIcon={<Lock size={16} />}
              isDisabled={loading}
              className="auth-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              style={{
                position: 'absolute',
                right: '12px',
                bottom: '12px',
                background: 'none',
                border: 'none',
                color: MUTED,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '4px'
              }}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          {!isSignUp && (
            <button type="button" className="auth-forgot-link" style={{ color: GOLD }}>
              Forgot password?
            </button>
          )}

          <PillButton
            variant="primary"
            type="submit"
            isDisabled={loading}
            label={loading ? 'Please wait...' : isSignUp ? 'Create Account' : 'Log in'}
            endContent={!loading && <Sparkles size={14} />}
            className="auth-submit-btn"
          />
        </form>

        <div className="auth-divider">
          <span style={{ flex: 1, height: '1px', backgroundColor: PANEL_BORDER }} />
          <span style={{ padding: '0 12px', color: MUTED, fontSize: '12px' }}>
            or continue with
          </span>
          <span style={{ flex: 1, height: '1px', backgroundColor: PANEL_BORDER }} />
        </div>

        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="auth-oauth-btn"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
              fill="#EA4335"
            />
          </svg>
          Continue with Google
        </button>

        <div style={{ marginTop: '24px', textAlign: 'center', fontSize: '13px' }}>
          <span style={{ color: MUTED }}>
            {isSignUp ? 'Already have an account? ' : 'New here? '}
          </span>
          <button
            type="button"
            onClick={() => {
              setIsSignUp(!isSignUp)
              setError(null)
            }}
            style={{
              background: 'none',
              border: 'none',
              color: GOLD,
              fontWeight: 600,
              cursor: 'pointer',
              padding: '0 4px'
            }}
          >
            {isSignUp ? 'Log in' : 'Sign up'}
          </button>
        </div>
      </div>
    </div>
  )
}
